import { onCleanup, createEffect, createSignal, untrack, createComputed } from 'solid-js';
import { isServer } from 'solid-js/web';

// src/index.ts
var makeTimer = (fn, delay, timer) => {
  if (isServer) {
    return () => void 0;
  }
  const intervalId = timer(fn, delay);
  const clear = () => clearInterval(intervalId);
  return onCleanup(clear);
};
var createTimer = (fn, delay, timer) => {
  if (isServer) {
    return void 0;
  }
  if (typeof delay === "number") {
    makeTimer(fn, delay, timer);
    return;
  }
  let done = false;
  let prevTime = performance.now();
  let fractionDone = 0;
  let shouldHandleFraction = false;
  const callHandler = () => {
    untrack(fn);
    prevTime = performance.now();
    done = timer === setTimeout;
  };
  createEffect((prevDelay) => {
    if (done)
      return;
    const currDelay = delay();
    if (currDelay === false) {
      if (prevDelay)
        fractionDone += (performance.now() - prevTime) / prevDelay;
      return currDelay;
    }
    if (prevDelay === false)
      prevTime = performance.now();
    if (shouldHandleFraction) {
      if (prevDelay)
        fractionDone += (performance.now() - prevTime) / prevDelay;
      prevTime = performance.now();
      if (fractionDone >= 1) {
        fractionDone = 0;
        callHandler();
      } else if (fractionDone > 0) {
        const [listen, rerunEffect] = createSignal(void 0, { equals: false });
        listen();
        makeTimer(
          () => {
            fractionDone = 0;
            shouldHandleFraction = false;
            rerunEffect();
            callHandler();
          },
          (1 - fractionDone) * currDelay,
          setTimeout
        );
        return currDelay;
      }
    }
    shouldHandleFraction = true;
    makeTimer(callHandler, currDelay, timer);
    return currDelay;
  });
};
var createTimeoutLoop = (handler, timeout) => {
  if (isServer) {
    return void 0;
  }
  if (typeof timeout === "number") {
    makeTimer(handler, timeout, setInterval);
    return;
  }
  const [currentTimeout, setCurrentTimeout] = createSignal(untrack(timeout));
  createEffect(() => {
    const currTimeout = currentTimeout();
    if (currTimeout === false)
      return;
    makeTimer(
      () => {
        handler();
        setCurrentTimeout(timeout);
      },
      currTimeout,
      setInterval
    );
  });
};
function createPolled(fn, timeout, value, options) {
  if (isServer) {
    return fn;
  }
  const [polled, setPolled] = createSignal(untrack(fn.bind(void 0, value)), options);
  const update = () => setPolled(fn);
  createTimer(update, timeout, setInterval);
  createComputed(update);
  return polled;
}
var createIntervalCounter = (timeout, options) => {
  if (isServer) {
    return () => 0;
  }
  return createPolled((prev) => prev + 1, timeout, -1, options);
};

export { createIntervalCounter, createPolled, createTimeoutLoop, createTimer, makeTimer };
